import React, { useMemo, useState } from 'react';
import {
  ArrowLeft, ArrowRight, Box, Check, ChevronLeft, ChevronRight, CirclePlay, Clock3, Code2,
  Copy, Cpu, Download, ExternalLink, FileAudio, Github, Layers3, Play, Sparkles, TerminalSquare, Waves,
} from 'lucide-react';
import { cx, UI } from '../gitShared';
import { CodeBlock } from '../CodeBlock';
import { cogShowcaseBySlug, cogShowcases, type CogShowcase } from './cogShowcaseData';

const codeFor = (cog: CogShowcase, surface: 'http' | 'cli' | 'mcp') => {
  const input = JSON.stringify(cog.input);
  if (surface === 'cli') return `app cogs run ${cog.template} \\\n  --input '${input}'`;
  if (surface === 'mcp') return JSON.stringify({ name: 'run_cog_template', arguments: { template: cog.template, input: cog.input } }, null, 2);
  return `curl -sX POST https://app.nz/api/cogs/run \\\n  -H "Authorization: Bearer $APP_NZ_KEY" \\\n  -H "Content-Type: application/json" \\\n  -d '${JSON.stringify({ template: cog.template, input: cog.input })}'`;
};

const Waveform = ({ split = false }: { split?: boolean }) => (
  <div className="grid w-full gap-3 px-6">
    {(split ? ['Vocals', 'Instrumental'] : ['Audio output']).map((label, row) => (
      <div key={label} className="rounded-xl border border-white/10 bg-black/35 p-3">
        <div className="mb-2 flex items-center justify-between text-[10px] font-bold uppercase tracking-[0.16em] text-white/45">
          <span>{label}</span><FileAudio size={13} />
        </div>
        <div className="flex h-14 items-center gap-[3px] overflow-hidden">
          {Array.from({ length: 44 }, (_, index) => (
            <span key={index} className={cx('w-1 shrink-0 rounded-full', row ? 'bg-violet-300/75' : 'bg-cyan-300/80')}
              style={{ height: `${12 + ((index * 17 + row * 23) % 42)}px` }} />
          ))}
        </div>
      </div>
    ))}
  </div>
);

const MotionPreview = () => (
  <div className="relative flex h-full min-h-[320px] items-center justify-center overflow-hidden bg-[radial-gradient(circle_at_50%_45%,rgba(34,211,238,.14),transparent_38%)]">
    <div className="absolute inset-x-12 bottom-[24%] h-px bg-gradient-to-r from-transparent via-cyan-300/30 to-transparent" />
    <svg viewBox="0 0 220 260" className="h-[75%] max-h-[300px] w-auto overflow-visible" aria-label="ARDY pose frame preview">
      <g fill="none" stroke="currentColor" strokeLinecap="round" strokeWidth="8" className="text-cyan-200 drop-shadow-[0_0_12px_rgba(34,211,238,.45)]">
        <circle cx="112" cy="38" r="21" fill="rgba(34,211,238,.12)" />
        <path d="M112 62 L105 132 M106 82 L62 112 M106 84 L158 99 M105 132 L70 205 M105 132 L151 201" />
      </g>
      {[['112','62'],['106','83'],['62','112'],['158','99'],['105','132'],['70','205'],['151','201']].map(([cxv, cyv], i) => <circle key={i} cx={cxv} cy={cyv} r="6" fill="#fff" />)}
    </svg>
    <div className="absolute bottom-5 left-5 right-5 flex items-center justify-between font-mono text-[10px] text-cyan-100/55">
      <span>frame 018 / 040</span><span>20 FPS · 22 joints</span>
    </div>
  </div>
);

const ImagePreview = ({ cog }: { cog: CogShowcase }) => {
  const kind = cog.kind;
  const source = cog.outputMedia || cog.inputImage;
  const isCutout = kind === 'cutout';
  const isDepth = kind === 'depth';
  const isVector = kind === 'vector';
  const isUpscale = kind === 'upscale';
  const isClean = kind === 'clean';
  return (
    <div className={cx('relative flex h-full min-h-[320px] items-center justify-center overflow-hidden p-7', isCutout && 'bg-[linear-gradient(45deg,#171717_25%,transparent_25%),linear-gradient(-45deg,#171717_25%,transparent_25%),linear-gradient(45deg,transparent_75%,#171717_75%),linear-gradient(-45deg,transparent_75%,#171717_75%)] bg-[length:28px_28px] bg-[position:0_0,0_14px,14px_-14px,-14px_0px]')}>
      {source ? <img src={source} alt={`${cog.title} output preview`} className={cx(
        'max-h-[360px] w-full max-w-[420px] rounded-2xl object-contain shadow-2xl transition',
        isUpscale && 'scale-105 contrast-110 saturate-110',
        isVector && 'contrast-125 saturate-150',
        isDepth && 'grayscale contrast-125',
        isCutout && 'drop-shadow-[0_18px_35px_rgba(0,0,0,.45)]',
      )} /> : <div className="relative aspect-[2/3] w-full max-w-[260px] overflow-hidden rounded-3xl border border-violet-300/20 bg-[radial-gradient(circle_at_48%_28%,rgba(244,114,182,.7),transparent_15%),radial-gradient(circle_at_55%_45%,rgba(99,102,241,.65),transparent_32%),linear-gradient(155deg,#17132e,#070912_72%)] shadow-[0_30px_90px_rgba(91,33,182,.28)]">
        <div className="absolute inset-x-5 bottom-5 rounded-xl border border-white/10 bg-black/35 p-3 backdrop-blur-md">
          <div className="text-[10px] font-black uppercase tracking-[0.2em] text-violet-200">Anima render slot</div>
          <div className="mt-1 text-xs leading-5 text-white/55">A generated image appears here after the run completes.</div>
        </div>
      </div>}
      {isDepth && <div className="pointer-events-none absolute inset-7 rounded-2xl bg-gradient-to-br from-fuchsia-500/35 via-cyan-300/25 to-blue-900/60 mix-blend-color" />}
      {isClean && <div className="absolute right-[24%] top-[42%] h-20 w-20 rounded-full border border-white/30 bg-cyan-300/15 shadow-[0_0_45px_rgba(34,211,238,.35)] backdrop-blur-md" />}
      {isVector && <div className="absolute bottom-5 right-5 rounded-lg border border-white/15 bg-black/70 px-3 py-2 font-mono text-[10px] text-cyan-200">&lt;path d=&quot;M…&quot; /&gt;</div>}
      {kind === 'voxel' && <div className="absolute inset-x-[12%] bottom-7 h-20 origin-bottom rotate-x-45 bg-[linear-gradient(rgba(34,211,238,.15)_1px,transparent_1px),linear-gradient(90deg,rgba(34,211,238,.15)_1px,transparent_1px)] bg-[size:20px_20px] [transform:perspective(400px)_rotateX(64deg)]" />}
    </div>
  );
};

const TextPreview = ({ cog }: { cog: CogShowcase }) => (
  <div className="flex h-full min-h-[430px] flex-col gap-3 bg-[linear-gradient(160deg,#0b1020,#05070f)] p-6">
    <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.16em] text-violet-300/75">
      <Sparkles size={13} /> assistant
    </div>
    <div className="min-h-0 flex-1 overflow-auto whitespace-pre-wrap rounded-2xl border border-white/10 bg-black/45 p-5 font-mono text-[12px] leading-6 text-white/80">
      {cog.outputText || String(cog.input.prompt || '')}
    </div>
  </div>
);

const OutputPreview = ({ cog }: { cog: CogShowcase }) => {
  if (cog.kind === 'text') return <TextPreview cog={cog} />;
  if (cog.kind === 'motion') return <MotionPreview />;
  if (cog.kind === 'speech') return <div className="flex h-full min-h-[320px] items-center"><Waveform /></div>;
  if (cog.kind === 'stems') return <div className="flex h-full min-h-[320px] items-center"><Waveform split /></div>;
  if (cog.kind === 'music') return (
    <div className="flex h-full min-h-[320px] flex-col items-center justify-center gap-4 p-6">
      <Waveform />
      {cog.outputMedia && <audio src={cog.outputMedia} controls className="w-full max-w-[420px]" />}
    </div>
  );
  if (cog.kind === 'video' && cog.outputMedia) return (
    <div className="relative flex h-full min-h-[430px] items-center justify-center overflow-hidden bg-black">
      <video src={cog.outputMedia} muted loop autoPlay playsInline controls className="h-full max-h-[440px] w-full object-cover" />
      <span className="pointer-events-none absolute left-4 top-4 inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-black/65 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-white/75"><CirclePlay size={12} /> preview</span>
    </div>
  );
  return <ImagePreview cog={cog} />;
};

const InputPreview = ({ cog }: { cog: CogShowcase }) => (
  <div className="grid min-h-[430px] grid-rows-[minmax(0,1fr)_auto] bg-white/[0.025]">
    <div className="relative flex min-h-0 flex-col items-center justify-center gap-4 overflow-hidden p-6">
      {cog.inputImage || cog.inputVideo ? (
        <div className={cx('grid w-full max-w-[560px] gap-3', cog.inputImage && cog.inputVideo && 'grid-cols-2')}>
          {cog.inputImage && <div className="min-w-0"><div className="mb-2 text-[9px] font-bold uppercase tracking-[0.15em] text-white/35">reference image</div><img src={cog.inputImage} alt={`${cog.title} reference input`} className="h-[240px] w-full rounded-2xl object-contain shadow-2xl" /></div>}
          {cog.inputVideo && <div className="min-w-0"><div className="mb-2 text-[9px] font-bold uppercase tracking-[0.15em] text-white/35">driving video</div><video src={cog.inputVideo} controls muted loop playsInline preload="metadata" className="h-[240px] w-full rounded-2xl bg-black object-contain shadow-2xl" /></div>}
        </div>
      ) : cog.kind === 'speech' || cog.kind === 'stems' ? (
        <Waveform />
      ) : (
        <div className="max-w-sm text-center">
          <Sparkles className="mx-auto mb-5 text-violet-300" size={48} />
          <p className="text-balance text-xl font-black leading-snug text-white/90">{String(cog.input.prompt || cog.input.text || cog.input.input || cog.input.style || '')}</p>
        </div>
      )}
      {cog.inputAudio && (
        <div className="w-full max-w-[360px] rounded-xl border border-white/10 bg-black/40 p-3">
          <div className="mb-2 flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.16em] text-white/45">
            <FileAudio size={13} /> driving audio
          </div>
          <audio src={cog.inputAudio} controls className="w-full" />
        </div>
      )}
      {cog.kind === 'clean' && <div className="absolute right-[25%] top-[35%] h-20 w-20 rounded-full border-2 border-dashed border-white bg-white/65" />}
    </div>
    <div className="border-t border-white/10 bg-black/25 p-4">
      <div className="grid gap-2 sm:grid-cols-2">
        {Object.entries(cog.input).slice(0, 6).map(([key, value]) => (
          <div key={key} className="min-w-0 rounded-lg border border-white/8 bg-white/[0.035] px-3 py-2">
            <div className="text-[9px] font-bold uppercase tracking-[0.15em] text-white/35">{key.replaceAll('_', ' ')}</div>
            <div className="mt-1 truncate font-mono text-[11px] text-white/72" title={String(value)}>{String(value)}</div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const SurfaceCode = ({ cog }: { cog: CogShowcase }) => {
  const [surface, setSurface] = useState<'http' | 'cli' | 'mcp'>('http');
  const code = codeFor(cog, surface);
  const lang = surface === 'mcp' ? 'json' : 'bash';
  return (
    <section className="overflow-hidden rounded-2xl border border-white/10 bg-[#080a0e]">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-white/10 px-4 py-3">
        <div className="flex gap-1 rounded-lg bg-white/[0.04] p-1">
          {(['http', 'cli', 'mcp'] as const).map((item) => (
            <button key={item} onClick={() => setSurface(item)} className={cx('rounded-md px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider transition', surface === item ? 'bg-white text-black' : 'text-white/45 hover:text-white')}>
              {item}
            </button>
          ))}
        </div>
      </div>
      <CodeBlock code={code} language={lang} className="rounded-none border-0" preClassName="rounded-none border-0 !text-[12px] !leading-6" />
    </section>
  );
};

export const CogShowcasePage: React.FC<{ slug: string }> = ({ slug }) => {
  const cog = cogShowcaseBySlug[slug];
  const index = useMemo(() => cog ? cogShowcases.indexOf(cog) : -1, [cog]);
  if (!cog) return (
    <main className="mx-auto min-h-[65vh] max-w-4xl px-5 py-24 text-center text-white">
      <Box size={48} className="mx-auto text-white/25" />
      <h1 className="mt-5 text-3xl font-black">Cog showcase not found</h1>
      <a href="/cogs" className={cx(UI.btn, UI.btnPrimary, 'mt-7')}><ArrowLeft size={15} /> Open Cog Studio</a>
    </main>
  );
  const previous = cogShowcases[(index - 1 + cogShowcases.length) % cogShowcases.length];
  const next = cogShowcases[(index + 1) % cogShowcases.length];
  return (
    <main className="overflow-hidden text-white">
      <section className="relative border-b border-white/8 px-5 pb-12 pt-10 sm:px-8 lg:pb-16 lg:pt-14">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_18%_20%,rgba(34,211,238,.14),transparent_28%),radial-gradient(circle_at_82%_10%,rgba(139,92,246,.13),transparent_28%)]" />
        <div className="relative mx-auto max-w-7xl">
          <a href="/cogs/examples" className="inline-flex items-center gap-2 text-xs font-semibold text-white/45 transition hover:text-white"><ChevronLeft size={15} /> All examples</a>
          <div className="mt-8 grid gap-8 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-full border border-cyan-300/20 bg-cyan-300/10 px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.18em] text-cyan-200">Example Cog</span>
                <span className="font-mono text-[11px] text-white/35">template:{cog.template}</span>
              </div>
              <p className="mt-6 text-sm font-bold uppercase tracking-[0.2em] text-violet-300/80">{cog.eyebrow}</p>
              <h1 className="mt-2 max-w-4xl text-balance text-4xl font-black tracking-[-0.045em] sm:text-6xl lg:text-7xl">{cog.title}</h1>
              <p className="mt-5 max-w-3xl text-pretty text-base leading-7 text-white/58 sm:text-lg">{cog.description}</p>
            </div>
            <div className="flex flex-wrap gap-2 lg:max-w-[380px] lg:justify-end">
              <a href={`/cogs?template=${encodeURIComponent(cog.template)}`} className={cx(UI.btn, UI.btnPrimary, 'h-11 px-5')}><Play size={16} /> Deploy & run</a>
              {cog.source && <a href={cog.source} target="_blank" rel="noreferrer" className={cx(UI.btn, UI.btnOutline, 'h-11 px-4')}><Github size={16} /> Source</a>}
            </div>
          </div>
          <div className="mt-8 flex flex-wrap gap-2 text-[11px] text-white/55">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5"><Cpu size={13} /> {cog.hardware}</span>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5"><Clock3 size={13} /> scale to zero</span>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5"><ArrowRight size={13} /> {cog.output}</span>
            {cog.tags.map((tag) => <span key={tag} className="rounded-full border border-white/8 px-3 py-1.5 text-white/38">{tag}</span>)}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-12 sm:px-8 lg:py-16">
        <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
          <div><p className={UI.label}>Canonical example</p><h2 className="mt-2 text-2xl font-black tracking-tight sm:text-3xl">Input and output, side by side</h2></div>
          <p className="max-w-md text-right text-xs leading-5 text-white/38">{cog.outputNote}</p>
        </div>
        <div className="grid overflow-hidden rounded-3xl border border-white/12 bg-[#090b0f] shadow-[0_35px_100px_rgba(0,0,0,.45)] lg:grid-cols-2">
          <div className="min-w-0 border-b border-white/10 lg:border-b-0 lg:border-r">
            <div className="flex items-center justify-between border-b border-white/10 px-4 py-3 text-[10px] font-black uppercase tracking-[0.16em] text-white/42"><span>01 · input</span><Code2 size={14} /></div>
            <InputPreview cog={cog} />
          </div>
          <div className="min-w-0">
            <div className="flex items-center justify-between border-b border-white/10 px-4 py-3 text-[10px] font-black uppercase tracking-[0.16em] text-white/42"><span>02 · {cog.output}</span>{cog.outputFile ? <a href={cog.outputFile} download className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/[0.05] px-2.5 py-1 text-white/70 transition hover:border-white/30 hover:text-white"><Download size={12} /> download output</a> : <Sparkles size={14} />}</div>
            <OutputPreview cog={cog} />
          </div>
        </div>

        <div className="mt-14 grid gap-6 lg:grid-cols-[minmax(0,1.2fr)_minmax(280px,.8fr)]">
          <div className="min-w-0">
            <div className="mb-4 flex items-center gap-2"><TerminalSquare size={17} className="text-cyan-300" /><h2 className="text-xl font-black">Run the same recipe everywhere</h2></div>
            <SurfaceCode cog={cog} />
          </div>
          <aside className="min-w-0 rounded-2xl border border-white/10 bg-white/[0.025] p-5">
            <p className={UI.label}>One deployment contract</p>
            <div className="mt-5 space-y-4 text-sm leading-6 text-white/55">
              <p className="flex min-w-0 gap-3 break-words"><Layers3 size={17} className="mt-1 shrink-0 text-violet-300" /> The browser, Go desktop app, CLI, and MCP tools use the same template name and input object.</p>
              <p className="flex min-w-0 gap-3 break-words"><Waves size={17} className="mt-1 shrink-0 text-cyan-300" /> The first request can wake an idle worker; later requests reuse it until the idle window closes.</p>
              <p className="flex min-w-0 gap-3 break-words"><Check size={17} className="mt-1 shrink-0 text-emerald-300" /> Outputs are returned as durable URLs or structured JSON, never provider pod identifiers.</p>
            </div>
            {cog.upstream && <a href={cog.upstream} target="_blank" rel="noreferrer" className="mt-6 inline-flex items-center gap-2 text-xs font-bold text-white/55 hover:text-white">Upstream model <ExternalLink size={13} /></a>}
          </aside>
        </div>

        <nav className="mt-14 grid gap-3 border-t border-white/10 pt-8 sm:grid-cols-2" aria-label="More Cog showcases">
          <a href={`/cogs/${previous.slug}`} className="group rounded-2xl border border-white/10 bg-white/[0.02] p-5 transition hover:border-white/20 hover:bg-white/[0.045]">
            <span className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-white/32"><ChevronLeft size={13} /> Previous</span>
            <span className="mt-2 block text-lg font-black group-hover:text-cyan-200">{previous.title}</span>
          </a>
          <a href={`/cogs/${next.slug}`} className="group rounded-2xl border border-white/10 bg-white/[0.02] p-5 text-right transition hover:border-white/20 hover:bg-white/[0.045]">
            <span className="flex items-center justify-end gap-1 text-[10px] font-bold uppercase tracking-wider text-white/32">Next <ChevronRight size={13} /></span>
            <span className="mt-2 block text-lg font-black group-hover:text-violet-200">{next.title}</span>
          </a>
        </nav>
      </section>
    </main>
  );
};
